import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { Shell } from '../ui/Layout';
import { ButtonLink } from '../ui/Button';
import { services } from '../../data/services';
import { whoWeServeColumns } from '../../data/whoWeServe';
const primaryNav = [{
  label: 'About Us',
  to: '/about'
}, {
  label: 'Executive Search',
  to: '/services',
  group: 'services' as const
}, {
  label: 'Who We Serve',
  to: '/who-we-serve',
  group: 'whoWeServe' as const
}, {
  label: 'Leadership Opportunities',
  to: '/opportunities'
}, {
  label: 'Career Services',
  to: '/career-services'
}, {
  label: 'Insights',
  to: '/insights'
}, {
  label: 'Contact',
  to: '/contact'
}];
export function SiteHeader() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<'services' | 'whoWeServe' | null>(null);
  const location = useLocation();
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setDrawerOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenGroup(null);
        setDrawerOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const groupColumns = openGroup === 'services' ? [{
    heading: 'Executive Search',
    headingTo: '/services',
    links: services.map(s => ({
      label: s.name,
      to: `/services/${s.slug}`
    }))
  }] : openGroup === 'whoWeServe' ? whoWeServeColumns : [];
  return <header className="on-dark relative z-40 bg-ink" data-id="element-487" data-mp="19m1jmut8nvt5">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-6 focus:top-3 focus:z-50 focus:bg-gold focus:px-4 focus:py-2 focus:font-sans focus:text-sm focus:text-ink" data-id="element-488" data-mp="3pal6xdc0q0dw">
        Skip to main content
      </a>
      <Shell data-id="element-489" data-mp="366zsxeqw2ryg">
        <div className="flex items-center justify-between gap-6 border-b border-navy-600 py-5" data-id="element-490" data-mp="3667za9gur4at">
          <Link to="/" className="group flex items-baseline gap-2 font-serif text-parchment" data-id="element-491" data-mp="31fhq0l5b2hau">
            <span className="text-[1.0625rem] tracking-[0.14em] uppercase" data-id="element-492" data-mp="2whga2936ey3l">Velnoris</span>
            <span aria-hidden="true" className="text-gold" data-id="element-493" data-mp="2rhljk6nbcaj2">|</span>
            <span className="text-[0.8125rem] tracking-[0.14em] uppercase text-navy-100 group-hover:text-parchment" data-id="element-494" data-mp="2rfqwalokk6bt">
              Executive Network
            </span>
          </Link>

          <div ref={navRef} className="hidden items-center gap-1 lg:flex" onMouseLeave={() => setOpenGroup(null)} data-id="element-495" data-mp="2ihoyef04ttsr">
            <nav aria-label="Primary" data-id="element-496" data-mp="2dk5ccsx70cq9">
              <ul className="flex items-center gap-1" data-id="element-497" data-mp="gqbvtwlmzxzv">
                {primaryNav.map(item => item.group ? <li key={item.to} data-id="element-498" data-mp="br8qsc5nprrw">
                      <button type="button" aria-expanded={openGroup === item.group} onClick={() => setOpenGroup(openGroup === item.group ? null : item.group!)} onMouseEnter={() => setOpenGroup(item.group!)} className="inline-flex items-center gap-1 px-3 py-2 font-sans text-[0.8125rem] text-navy-100 transition-colors duration-120 hover:text-parchment" data-id="element-499" data-mp="ja89eflanfa7">
                        {item.label}
                        <ChevronDownIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-500" data-mp="59nsjoyxvtjc" />
                      </button>
                    </li> : <li key={item.to} data-id="element-501" data-mp="eb5dplr7elew">
                      <NavLink to={item.to} className={({
                  isActive
                }) => `px-3 py-2 font-sans text-[0.8125rem] transition-colors duration-120 ${isActive ? 'text-gold' : 'text-navy-100 hover:text-parchment'}`} data-id="element-502" data-mp="ec6p5rc4t9x5">
                        {item.label}
                      </NavLink>
                    </li>)}
              </ul>
            </nav>
            <div className="ml-3 flex items-center gap-2 border-l border-navy-600 pl-4" data-id="element-503" data-mp="1y4yhf44icz2d">
              <Link to="/sign-in" className="px-2 py-2 font-sans text-[0.8125rem] text-navy-100 transition-colors duration-120 hover:text-parchment" data-id="element-504" data-mp="1taawtgcmkot2">
                Sign In
              </Link>
              <ButtonLink to="/register" size="sm" variant="onDark" data-id="element-505" data-mp="2gy2burpjvfy1">
                Register
              </ButtonLink>
            </div>
          </div>

          <button type="button" className="inline-flex items-center gap-2 border border-navy-600 px-3 py-2 font-sans text-[0.8125rem] text-parchment lg:hidden" aria-expanded={drawerOpen} aria-controls="mobile-nav" onClick={() => setDrawerOpen(v => !v)} data-id="element-506" data-mp="2c0snlfdwdabs">
            {drawerOpen ? <XIcon aria-hidden="true" className="h-4 w-4" data-id="element-507" data-mp="39ksigqqntwsc" /> : <MenuIcon aria-hidden="true" className="h-4 w-4" data-id="element-508" data-mp="39i6f73asy3fx" />}
            Menu
          </button>
        </div>
      </Shell>

      {openGroup && groupColumns.length ? <div className="absolute inset-x-0 hidden animate-fade-in border-b border-navy-600 bg-navy-800 lg:block" onMouseEnter={() => setOpenGroup(openGroup)} onMouseLeave={() => setOpenGroup(null)} data-id="element-509" data-mp="30i9kajxtk8cf">
          <Shell className="py-8" data-id="element-510" data-mp="2qn9azrjuom81">
            <div className={`grid gap-x-12 gap-y-8 ${groupColumns.length > 1 ? 'grid-cols-3' : 'grid-cols-1'}`} data-id="element-511" data-mp="2cut24a49ptz7">
              {groupColumns.map(column => <nav key={column.heading} aria-label={column.heading} data-id="element-512" data-mp="2gt8wiz2oi4mn">
                  {groupColumns.length > 1 ? <h2 className="font-sans text-micro uppercase tracking-[0.14em] text-gold" data-id="element-513" data-mp="27unh3csi75q3">
                      {column.headingTo ? <Link to={column.headingTo} className="transition-colors duration-120 hover:text-gold-200" data-id="element-514" data-mp="dfhzt2endhan">
                          {column.heading}
                        </Link> : column.heading}
                    </h2> : null}
                  <ul className={`${groupColumns.length > 1 ? 'mt-4' : ''} grid gap-x-10 gap-y-1 ${groupColumns.length > 1 ? 'grid-cols-1' : 'grid-cols-2 xl:grid-cols-3'}`} data-id="element-515" data-mp="3vqv0x7i3e515">
                    {column.links.map(g => <li key={`${column.heading}-${g.label}`} data-id="element-516" data-mp="nepbzm9nb5w6">
                        <Link to={g.to} className="block border-b border-navy-600/60 py-2.5 font-serif text-[0.9375rem] text-navy-100 transition-colors duration-120 hover:text-gold-200" data-id="element-517" data-mp="id9rpwfal3eu">
                          {g.label}
                        </Link>
                      </li>)}
                  </ul>
                </nav>)}
            </div>
          </Shell>
        </div> : null}

      {drawerOpen ? <div id="mobile-nav" className="animate-fade-in border-b border-navy-600 bg-navy-800 lg:hidden" data-id="element-518" data-mp="32w6rammsz9tt">
          <Shell className="py-6" data-id="element-519" data-mp="37qt3gs0pl6ey">
            <nav aria-label="Primary, mobile" data-id="element-520" data-mp="3brcxvntwdvxe">
              <ul className="space-y-1" data-id="element-521" data-mp="3bso1nnncf3oi">
                {[{
              label: 'Home',
              to: '/'
            }, ...primaryNav].map(item => <li key={item.to} data-id="element-522" data-mp="3lorcdhw9gan2">
                    <Link to={item.to} className="block border-b border-navy-600/60 py-3 font-serif text-base text-navy-100" data-id="element-523" data-mp="3lpspdrwu777f">
                      {item.label}
                    </Link>
                  </li>)}
              </ul>
            </nav>
            {whoWeServeColumns.map(column => <div key={column.heading} className="mt-6" data-id="element-524" data-mp="21fo38k7i364">
                <h2 className="font-sans text-micro uppercase tracking-[0.14em] text-gold" data-id="element-525" data-mp="1zw8lk6djw5e">{column.heading}</h2>
                <ul className="mt-2" data-id="element-526" data-mp="3t6fqfjamomd7">
                  {column.links.map(l => <li key={`${column.heading}-${l.label}`} data-id="element-527" data-mp="btv625vi6rr0">
                      <Link to={l.to} className="block border-b border-navy-600/60 py-2.5 font-serif text-[0.9375rem] text-navy-100" data-id="element-528" data-mp="6tqpygyibz1b">
                        {l.label}
                      </Link>
                    </li>)}
                </ul>
              </div>)}
            <div className="mt-6 flex gap-3" data-id="element-529" data-mp="3k7u217ih6d4i">
              <ButtonLink to="/sign-in" variant="onDark" size="sm" full data-id="element-530" data-mp="3k60tfmoaxitv">
                Sign In
              </ButtonLink>
              <ButtonLink to="/register" size="sm" full data-id="element-531" data-mp="13l1b90q2zfn5">
                Register
              </ButtonLink>
            </div>
          </Shell>
        </div> : null}
    </header>;
}